// Shared helpers for the /agents catalog page (and any surface that
// needs the same agent-presentation vocabulary). Framework-free so it
// can be imported by any component without dragging React in.

// Per-category color palette. Hashed from `slug` so the same category
// stays the same color across renders. Picked deeper than Tailwind 500s
// so the colors stay legible on top of a 12–20% alpha tint background.
//
// Sized to 32 swatches so that catalogs with many categories
// (~30 deck recipe categories, ~12 model categories, etc.) can each land on
// a distinct hue. Ordered to alternate hue families so the *most common*
// slugs (which tend to cluster low in the modulo) don't all land on
// adjacent blues / greens.
const CATEGORY_PALETTE = [
  '#2563EB', // blue-600
  '#16A34A', // green-600
  '#DB2777', // pink-600
  '#7C3AED', // violet-600
  '#D97706', // amber-600
  '#0891B2', // cyan-600
  '#9333EA', // purple-600
  '#0D9488', // teal-600
  '#0284C7', // sky-600
  '#DC2626', // red-600
  '#E11D48', // rose-600
  '#059669', // emerald-600
  '#4F46E5', // indigo-600
  '#C026D3', // fuchsia-600
  '#EA580C', // orange-600
  '#65A30D', // lime-600
  '#1D4ED8', // blue-700
  '#15803D', // green-700
  '#BE185D', // pink-700
  '#6D28D9', // violet-700
  '#B45309', // amber-700
  '#0E7490', // cyan-700
  '#7E22CE', // purple-700
  '#115E59', // teal-700
  '#0369A1', // sky-700
  '#B91C1C', // red-700
  '#BE123C', // rose-700
  '#047857', // emerald-700
  '#4338CA', // indigo-700
  '#A21CAF', // fuchsia-700
  '#C2410C', // orange-700
  '#4D7C0F', // lime-700
];

// Editorial overrides — keep tool / deck / agent categories on the exact
// hex they shipped with so the hero gradient, list cards, and any other
// surface that hashes the same slug all agree. Add new entries here
// instead of branching in callers.
const KNOWN_CATEGORY_COLORS = {
  // Tool categories (mirrors what `toolDetailHelpers.CATEGORY_COLORS` shipped with).
  create:   '#9333EA',
  edit:     '#3B82F6',
  write:    '#22C55E',
  analyze:  '#CA8A04',
  code:     '#EC4899',
  business: '#F97316',
};

const FALLBACK_CATEGORY_COLOR = '#6B7280';

// FNV-1a 32-bit. Cheap, deterministic, and gives the larger palette an
// even spread across short / similar slugs (the old `h*31+c` hash ended
// up clustering on the low bits, so neighbouring categories were landing
// on the same swatch).
function hashSlug(s) {
  let hash = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash;
}

export const colorForSlug = (slug) => {
  const raw = slug == null ? '' : String(slug).trim().toLowerCase();
  if (!raw) return FALLBACK_CATEGORY_COLOR;
  if (KNOWN_CATEGORY_COLORS[raw]) return KNOWN_CATEGORY_COLORS[raw];
  return CATEGORY_PALETTE[hashSlug(raw) % CATEGORY_PALETTE.length];
};

export function hexToRgba(hex, alpha) {
  const raw = String(hex || '')
    .trim()
    .replace('#', '');
  if (!raw) return `rgba(107, 114, 128, ${alpha})`;
  const full = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (full.length !== 6) return `rgba(107, 114, 128, ${alpha})`;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) return `rgba(107, 114, 128, ${alpha})`;
  return `rgba(${r},${g},${b},${alpha})`;
}

/**
 * Single source of truth for hero / card palettes. Anything that wants the
 * "fg / bg / soft" shape (tool detail hero, deck columns, etc.) should
 * derive from this so every surface for the same slug matches `colorForSlug`.
 */
export const paletteForSlug = (slug) => {
  const hex = colorForSlug(slug);
  return {
    fg: hexToRgba(hex, 1),
    bg: hexToRgba(hex, 0.12),
    soft: hexToRgba(hex, 0.06),
  };
};

/**
 * Hero wash + blob rgba stops derived from the same slug hash as `colorForSlug`
 * (agents, task templates, any surface using this catalog ring).
 */
export const heroChromaForSlug = (slug) => {
  const hex = colorForSlug(slug);
  return {
    gradSoft: hexToRgba(hex, 0.09),
    gradMid: hexToRgba(hex, 0.045),
    blobA: hexToRgba(hex, 0.065),
    blobB: hexToRgba(hex, 0.04),
  };
};

/** Soft fill for breadcrumb hover (tinted, not a solid accent block). */
export const breadcrumbHoverTintForSlug = (slug) => hexToRgba(colorForSlug(slug), 0.12);

/** Opaque accent for hover surfaces (breadcrumb pills, etc.). */
export const hoverSurfaceForSlug = (slug) => colorForSlug(slug);

// Same 7-day "new" rule the rest of the app uses.
export const isNewAgent = (agent) => {
  if (!agent?.createdAt) return false;
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  return new Date(agent.createdAt) > cutoff;
};

// Compact format for conversation counts. 1234 → "1.2k".
export const formatCount = (n) => {
  if (!n || n < 1000) return String(n || 0);
  if (n < 1_000_000) {
    const v = n / 1000;
    return `${v >= 10 ? Math.round(v) : v.toFixed(1)}k`;
  }
  return `${(n / 1_000_000).toFixed(1)}M`;
};

// Output kind → small icon. Output types come from the agent serializer
// as a string array (`'text' | 'image' | ...`).
export const outputKindIcon = (kind) => {
  switch (kind) {
    case 'image': return 'image';
    case 'audio': return 'music';
    case 'video': return 'video';
    case 'file':  return 'file';
    case 'text':
    default:      return 'align-left';
  }
};

// Pick the highest-signal output type so cards can show one focused
// chip instead of a salad of icons.
export const primaryOutputKind = (agent) => {
  const types = Array.isArray(agent?.outputTypes) ? agent.outputTypes : [];
  return types.find(t => t === 'image')
      || types.find(t => t === 'video')
      || types.find(t => t === 'audio')
      || types[0]
      || 'text';
};

// Flatten `categoriesWithAgentIds` into a deduped list of agents in
// store order. Used by the All view to derive featured + section data.
export const flattenAgentsFromCategories = (categoriesWithAgentIds, getAgentById) => {
  const seen = new Set();
  const out = [];
  (categoriesWithAgentIds || []).forEach((cat) => {
    (cat?.agentIds || []).forEach((id) => {
      if (!id || seen.has(id)) return;
      const agent = getAgentById(id);
      if (!agent) return;
      seen.add(id);
      out.push(agent);
    });
  });
  return out;
};

// Editorial flag — either of the "featured" toggles on the Agent model.
export const isFeaturedAgent = (agent) =>
  !!agent?.flags?.isFeatured || !!agent?.flags?.isFeaturedOnCharactersPage;

/**
 * Catalog list payloads often leave `category` as an ObjectId (not populated).
 * Stamp a `{ id, slug, name }` from the parent section so cards can show a label.
 */
export const attachAgentCategory = (agent, categoryMeta) => {
  if (!agent) return agent;
  if (agent.category && typeof agent.category === 'object' && agent.category.name) return agent;
  if (!categoryMeta?.name && !categoryMeta?.slug) return agent;
  return {
    ...agent,
    category: {
      id: categoryMeta.id || categoryMeta.slug,
      slug: categoryMeta.slug || categoryMeta.id,
      name: categoryMeta.name || categoryMeta.slug || 'Agent',
    },
  };
};
